import { ReportReviewerKind, ReportReviewerStatus } from "@api/graphql/types-and-hooks";
import { getReviewUserDisplay } from "@components/QaReview/components/commentAuthor";
import { Reviewer } from "@components/ReportReview/reviewers";

export const NO_REVIEW_REQUESTED_TOOLTIP = "Request a review and get it approved before you can complete this report.";
export const APPROVAL_LOADING_TOOLTIP = "Checking review approvals before you can complete this report.";

type ActiveReviewer = Pick<Reviewer, "id" | "type" | "status" | "reviewer">;

export const resolveReviewSetting = (
  templateValue: boolean | null | undefined,
  organizationValue: boolean | undefined,
) => templateValue ?? organizationValue ?? false;

const namesOf = (reviewers: ActiveReviewer[]) =>
  reviewers.map(({ reviewer }) => getReviewUserDisplay(reviewer ?? null).name).join(", ");

interface ApprovalBlockArgs {
  isRequired: boolean;
  isLoaded: boolean;
  reviewers: ActiveReviewer[];
}

export const getApprovalBlockReason = ({ isRequired, isLoaded, reviewers }: ApprovalBlockArgs): string | undefined => {
  if (!isRequired) return undefined;
  if (!isLoaded) return APPROVAL_LOADING_TOOLTIP;
  if (!reviewers.length) return NO_REVIEW_REQUESTED_TOOLTIP;

  const changesRequested = reviewers.filter(({ status }) => status === ReportReviewerStatus.ChangesRequested);
  if (changesRequested.length) {
    return `${namesOf(changesRequested)} requested changes. Address them and re-request review.`;
  }
  const waiting = reviewers.filter(({ status }) => status !== ReportReviewerStatus.Approved);
  if (waiting.length) return `Waiting for approval from ${namesOf(waiting)}.`;
  return undefined;
};

interface FinalApproverWaitArgs {
  waitsForReviewers: boolean;
  myId: string | undefined;
  reviewers: ActiveReviewer[];
}

export const getFinalApproverWaitReason = ({
  waitsForReviewers,
  myId,
  reviewers,
}: FinalApproverWaitArgs): string | undefined => {
  if (!waitsForReviewers || !myId) return undefined;
  const me = reviewers.find(({ reviewer }) => reviewer?.id === myId);
  if (me?.type !== ReportReviewerKind.FinalApprover) return undefined;

  const waiting = reviewers.filter(({ id, status }) => id !== me.id && status !== ReportReviewerStatus.Approved);
  if (!waiting.length) return undefined;
  return `You can approve once ${namesOf(waiting)} ${waiting.length > 1 ? "have" : "has"} approved.`;
};
